import i18n from '@/i18n'

/**
 * Helpers de formateo de fechas y titulos para "Exportar Recibos de pago".
 *
 * Aisla toda la logica de construccion del displayName de una nomina (titulo
 * de la card / nombre de la exportacion), su subtitulo (rango de fechas), la
 * fecha de cierre y el formateo de fechas de las exportaciones recientes.
 */

/* Nombres de meses en espanol (para armar el titulo de la nomina) */
const MONTHS_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
]

const MONTHS_SHORT_ES = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic'
]

/* Codigos de frecuencia de la tabla frequencies (ver FrequencyEnum backend) */
const FREQUENCY = {
  WEEKLY: 'S',
  BIWEEKLY: 'BS',
  MONTHLY: 'M',
  FORTNIGHTLY: 'Q'
} as const

/* Codigo del proceso de nomina regular (sin prefijo en el titulo) */
const PAYROLL_PROCESS_CODE = 'NOMINA'

/* True si el codigo de proceso corresponde a la nomina regular */
export const isPayrollProcess = (code?: string): boolean =>
  code === PAYROLL_PROCESS_CODE

/* Separador entre el prefijo del proceso y la fecha (ej: "Extra · 26 Abr 2026") */
const PREFIX_SEPARATOR = ' · '

/* Datos minimos de una nomina cerrada necesarios para construir su titulo */
export interface PayrollTitleInput {
  name: string
  code: string
  start_date: string
  end_date: string
  /* Codigo de la frecuencia del payroll (S/BS/M/Q) */
  frequency: string
}

/* Parsea una fecha (yyyy-mm-dd o ISO) a Date, o null si es invalida */
export const parseDate = (value?: string): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return isNaN(date.getTime()) ? null : date
}

/* dia con dos digitos: 5 -> "05" */
const dd = (date: Date): string => String(date.getUTCDate()).padStart(2, '0')

/* "Abril 2026" */
const monthYear = (date: Date): string =>
  `${MONTHS_ES[date.getUTCMonth()]} ${date.getUTCFullYear()}`

/* "26 Abr 2026" */
const dayMonthYear = (date: Date): string =>
  `${dd(date)} ${MONTHS_SHORT_ES[date.getUTCMonth()]} ${date.getUTCFullYear()}`

/* "01/04/2026" */
const slashDate = (date: Date): string =>
  `${dd(date)}/${String(date.getUTCMonth() + 1).padStart(
    2,
    '0'
  )}/${date.getUTCFullYear()}`

/**
 * Fecha o rango legible para el sufijo de procesos que NO son nomina:
 *  - un solo dia (start == end): "26 Abr 2026"
 *  - rango mismo mes/anio:       "20–25 Abr 2026"
 *  - rango cruzando mes/anio:    "20 Abr – 2 May 2026"
 */
const buildEventDate = (start: Date, end: Date): string => {
  const sameDay =
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth() &&
    start.getUTCDate() === end.getUTCDate()
  if (sameDay) return dayMonthYear(end)

  const sameMonth =
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth()
  if (sameMonth) {
    return `${dd(start)}–${dd(end)} ${
      MONTHS_SHORT_ES[end.getUTCMonth()]
    } ${end.getUTCFullYear()}`
  }
  return `${dayMonthYear(start)} – ${dayMonthYear(end)}`
}

/**
 * Titulo de una nomina regular segun su frecuencia:
 *  - Quincenal (Q): "1Q Abril 2026" / "2Q Abril 2026" (segun el dia de inicio)
 *  - Mensual (M):   "Abril 2026"
 *  - Otras (S/BS):  rango de fechas como fallback
 */
const buildPayrollTitle = (
  start: Date,
  end: Date,
  frequency?: string
): string => {
  if (frequency === FREQUENCY.FORTNIGHTLY) {
    /* Dia de inicio <= 15 => 1Q, si no => 2Q */
    const quincena = start.getUTCDate() <= 15 ? '1Q' : '2Q'
    return `${quincena} ${monthYear(start)}`
  }
  if (frequency === FREQUENCY.MONTHLY) {
    return monthYear(start)
  }
  /* Semanal / bisemanal / desconocida: mostrar el rango */
  return buildEventDate(start, end)
}

/**
 * Nombre a mostrar como TITULO de la card (displayName de la exportacion).
 *  - Nomina regular: depende de la frecuencia (1Q/2Q/mensual).
 *  - Otro proceso:   "<nombre del proceso> · <fecha/rango>".
 */
export const buildTitle = (row: PayrollTitleInput): string => {
  const start = parseDate(row.start_date)
  const end = parseDate(row.end_date)
  if (!start || !end) return row.name || '—'

  if (row.code === PAYROLL_PROCESS_CODE) {
    return buildPayrollTitle(start, end, row.frequency)
  }
  return `${row.name}${PREFIX_SEPARATOR}${buildEventDate(start, end)}`
}

/* Subtitulo: rango de fechas real de la nomina, ej "01/04/2026 – 15/04/2026" */
export const buildDateRange = (
  startDate?: string,
  endDate?: string
): string => {
  const start = parseDate(startDate)
  const end = parseDate(endDate)
  if (!start || !end) return ''
  if (
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth() &&
    start.getUTCDate() === end.getUTCDate()
  ) {
    return slashDate(end)
  }
  return `${slashDate(start)} – ${slashDate(end)}`
}

/* Fecha de cierre legible: "30 may 2026" */
export const buildDate = (closedDate?: string): string => {
  const date = parseDate(closedDate)
  if (!date) return '—'
  return dayMonthYear(date)
}

/* "2026-04-30": ordenable y seguro para nombres de archivo */
export const isoDate = (value?: string): string => {
  const date = parseDate(value)
  if (!date) return ''
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
    2,
    '0'
  )}-${dd(date)}`
}

/* Fecha/hora legible para la columna de la tabla, ej "07 jul 2026, 14:29" */
export const formatExportDate = (value?: string): string => {
  const date = parseDate(value)
  if (!date) return '—'
  return `${dayMonthYear(date)}, ${String(date.getHours()).padStart(
    2,
    '0'
  )}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * Tiempo relativo legible respecto a ahora, ej "Hace 5 minutos" / "5 minutes ago".
 * Usa Intl.RelativeTimeFormat con el locale activo del i18n.
 */
export const relativeTime = (value?: string): string => {
  const date = parseDate(value)
  if (!date) return ''

  const locale = i18n.global.locale.value || 'es'
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000)
  const abs = Math.abs(diffSeconds)

  if (abs < 60) return rtf.format(Math.round(diffSeconds), 'second')
  if (abs < 3600) return rtf.format(Math.round(diffSeconds / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diffSeconds / 3600), 'hour')
  return rtf.format(Math.round(diffSeconds / 86400), 'day')
}
